import React, { useState } from 'react';
import { POI_SUGGESTIONS, ICONS } from '../data/icons';
import POICard from './POICard';

function fmtRadius(v, useMetric) {
  if (useMetric) return v >= 1000 ? `${(v / 1000).toFixed(1)} km` : `${v} m`;
  const mi = v / 1609.34;
  return mi < 0.1 ? `${Math.round(mi * 5280)} ft` : `${mi.toFixed(2)} mi`;
}

export default function Sidebar({
  isDark, toggleTheme,
  mainLatLng, onSearch,
  radius, onRadiusChange,
  useMetric, onToggleUnit,
  pois, onAddManualPOI, onRemovePOI, onUpdateName, onIconClick, onFocusPOI,
  onFetchSuggestion, onExport,
}) {
  const [addressInput, setAddressInput] = useState('');
  const [searching, setSearching] = useState(false);

  async function handleSearch() {
    if (!addressInput.trim()) return;
    setSearching(true);
    await onSearch(addressInput.trim());
    setSearching(false);
  }

  const radiusPct = (((radius - 402) / (8047 - 402)) * 100).toFixed(1);

  return (
    <aside style={{
      width: 'var(--sidebar-w)', flexShrink: 0, background: 'var(--surface)',
      borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column',
      height: '100vh', overflow: 'hidden', position: 'relative', zIndex: 10,
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 20px', height: 'var(--header-h)', borderBottom: '1px solid var(--border)', flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 32, height: 32, background: 'var(--accent)', borderRadius: 'var(--r-sm)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15,
            boxShadow: '0 2px 8px rgba(99,102,241,.35)',
          }}>📍</div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, letterSpacing: '-0.3px', color: 'var(--text-1)' }}>POI Map</div>
            <div style={{ fontSize: 10, fontWeight: 500, color: 'var(--text-3)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Points of Interest</div>
          </div>
        </div>
        <button onClick={toggleTheme} title="Toggle dark mode" style={{
          width: 32, height: 32, background: 'var(--accent-dim)', border: '1.5px solid var(--accent)',
          borderRadius: 'var(--r-sm)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', color: 'var(--accent)',
        }}>
          {isDark
            ? <svg viewBox="0 0 24 24" style={{ width: 16, height: 16, fill: 'currentColor' }}><path d="M12 3a9 9 0 1 0 9 9c0-.46-.04-.92-.1-1.36a5.389 5.389 0 0 1-4.4 2.26 5.403 5.403 0 0 1-3.14-9.8c-.44-.06-.9-.1-1.36-.1z"/></svg>
            : <svg viewBox="0 0 24 24" style={{ width: 16, height: 16, fill: 'currentColor' }}><path d="M12 7c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5zM2 13h2c.55 0 1-.45 1-1s-.45-1-1-1H2c-.55 0-1 .45-1 1s.45 1 1 1zm18 0h2c.55 0 1-.45 1-1s-.45-1-1-1h-2c-.55 0-1 .45-1 1s.45 1 1 1zM11 2v2c0 .55.45 1 1 1s1-.45 1-1V2c0-.55-.45-1-1-1s-1 .45-1 1zm0 18v2c0 .55.45 1 1 1s1-.45 1-1v-2c0-.55-.45-1-1-1s-1 .45-1 1zM5.99 4.58a.996.996 0 0 0-1.41 0 .996.996 0 0 0 0 1.41l1.06 1.06c.39.39 1.03.39 1.41 0 .39-.39.39-1.02 0-1.41L5.99 4.58zm12.37 12.37a.996.996 0 0 0-1.41 0 .996.996 0 0 0 0 1.41l1.06 1.06c.39.39 1.03.39 1.41 0a.996.996 0 0 0 0-1.41l-1.06-1.06zm1.06-12.37l-1.06 1.06a.996.996 0 0 0 0 1.41c.39.39 1.03.39 1.41 0l1.06-1.06a.996.996 0 0 0 0-1.41.996.996 0 0 0-1.41 0zM7.05 18.36l-1.06-1.06a.996.996 0 0 0-1.41 0 .996.996 0 0 0 0 1.41l1.06 1.06c.39.39 1.03.39 1.41 0 .39-.39.39-1.03 0-1.41z"/></svg>
          }
        </button>
      </div>

      {/* Body */}
      <div style={{
        flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: 16,
        display: 'flex', flexDirection: 'column', gap: 12,
      }}>

        {/* Location Search */}
        <Section label="Location">
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <div style={{ flex: 1, position: 'relative' }}>
              <svg style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', width: 15, height: 15, opacity: 0.4, pointerEvents: 'none', fill: 'var(--text-1)' }} viewBox="0 0 24 24">
                <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
              </svg>
              <input
                type="text" value={addressInput}
                onChange={e => setAddressInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                placeholder="Search address or place…"
                style={{
                  width: '100%', padding: '9px 12px 9px 34px', background: 'var(--surface)',
                  border: '1.5px solid var(--border)', borderRadius: 'var(--r-md)',
                  fontFamily: 'var(--font)', fontSize: 13, color: 'var(--text-1)', outline: 'none',
                }}
              />
            </div>
            <button onClick={handleSearch} disabled={searching} style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 16px',
              border: 'none', borderRadius: 'var(--r-md)', fontFamily: 'var(--font)',
              fontSize: '12.5px', fontWeight: 600, cursor: 'pointer',
              background: 'var(--accent)', color: '#fff',
              boxShadow: '0 2px 8px rgba(99,102,241,.3)',
              opacity: searching ? 0.7 : 1,
            }}>
              {searching ? <span style={{ display: 'inline-block', width: 12, height: 12, border: '2px solid rgba(255,255,255,.35)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin .65s linear infinite' }} /> : 'Go'}
            </button>
          </div>
        </Section>

        {/* Radius */}
        <Section label="Search Radius" extra={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--accent)' }}>{fmtRadius(radius, useMetric)}</span>
            <div onClick={onToggleUnit} style={{
              display: 'flex', background: 'var(--surface-2)', border: '1.5px solid var(--border)',
              borderRadius: 'var(--r-pill)', overflow: 'hidden', cursor: 'pointer', userSelect: 'none',
            }}>
              {['km', 'mi'].map(u => (
                <span key={u} style={{
                  fontSize: '10.5px', fontWeight: 600, padding: '2px 8px', color: 'var(--text-3)',
                  borderRadius: 'var(--r-pill)',
                  ...(useMetric === (u === 'km') ? { background: 'var(--accent)', color: '#fff' } : {}),
                }}>{u}</span>
              ))}
            </div>
          </div>
        }>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 11, color: 'var(--text-3)' }}>{useMetric ? '0.4 km' : '0.25 mi'}</span>
            <div style={{ flex: 1, position: 'relative', height: 4, background: 'var(--border)', borderRadius: 4 }}>
              <input type="range" min="402" max="8047" step="100" value={radius}
                onChange={e => onRadiusChange(Number(e.target.value))}
                style={{
                  WebkitAppearance: 'none', width: '100%', height: 4, background: 'transparent',
                  cursor: 'pointer', position: 'absolute', inset: 0, margin: 0,
                  '--pct': radiusPct + '%',
                }}
              />
            </div>
            <span style={{ fontSize: 11, color: 'var(--text-3)' }}>{useMetric ? '8 km' : '5 mi'}</span>
          </div>
        </Section>

        {/* Suggestions */}
        {mainLatLng && (
          <Section label="Suggested Nearby" dotColor="var(--green)">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {POI_SUGGESTIONS.map(s => (
                <div key={s.key} onClick={() => onFetchSuggestion(s)} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 10px',
                  background: 'var(--surface)', border: '1.5px solid var(--border)',
                  borderRadius: 'var(--r-pill)', fontSize: '11.5px', fontWeight: 500,
                  color: 'var(--text-2)', cursor: 'pointer',
                }}>
                  <svg viewBox="0 0 24 24" style={{ width: 13, height: 13, fill: 'currentColor', flexShrink: 0 }}
                    dangerouslySetInnerHTML={{ __html: ICONS[s.key]?.svg || '' }} />
                  {s.label}
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* POI List */}
        <Section label="Points of Interest" dotColor="var(--amber)" extra={
          <button onClick={onAddManualPOI} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 10px',
            border: '1px solid transparent', borderRadius: 'var(--r-sm)', fontFamily: 'var(--font)',
            fontSize: '11.5px', fontWeight: 600, cursor: 'pointer',
            background: 'var(--accent-dim)', color: 'var(--accent)',
          }}>+ Add</button>
        }>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '0 0 2px' }}>
            {pois.length === 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '24px 16px', gap: 8, textAlign: 'center' }}>
                <div style={{ width: 40, height: 40, background: 'var(--accent-dim)', borderRadius: 'var(--r-md)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg viewBox="0 0 24 24" style={{ width: 20, height: 20, fill: 'var(--accent)' }}><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-1)' }}>No POIs yet</div>
                <div style={{ fontSize: 12, color: 'var(--text-3)', lineHeight: 1.5 }}>Search a location, then tap a suggestion chip to auto-add nearby places.</div>
              </div>
            ) : pois.map((poi, i) => (
              <POICard
                key={poi.id} poi={poi} index={i} useMetric={useMetric}
                onNameChange={onUpdateName} onIconClick={onIconClick}
                onFocus={onFocusPOI} onRemove={onRemovePOI}
              />
            ))}
          </div>
        </Section>

        {/* Export */}
        <Section label="Export" dotColor="var(--green)">
          <button onClick={onExport} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            width: '100%', padding: '9px 16px', border: 'none', borderRadius: 'var(--r-md)',
            fontFamily: 'var(--font)', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer',
            background: 'var(--accent)', color: '#fff', boxShadow: '0 2px 8px rgba(99,102,241,.3)',
          }}>
            <svg viewBox="0 0 24 24" style={{ width: 14, height: 14, fill: '#fff' }}><path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/></svg>
            Export as Image
          </button>
          <p style={{ fontSize: '11.5px', color: 'var(--text-3)', lineHeight: 1.5, marginTop: 8 }}>
            Downloads a PNG of the current map view + POI legend.
          </p>
        </Section>

      </div>
    </aside>
  );
}

function Section({ label, dotColor = 'var(--accent)', extra, children }) {
  return (
    <div style={{
      background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--r-lg)',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 16px', borderBottom: '1px solid var(--border)',
        borderRadius: 'var(--r-lg) var(--r-lg) 0 0',
      }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-3)' }}>
          <span style={{ width: 6, height: 6, background: dotColor, borderRadius: '50%', display: 'inline-block' }} />
          {label}
        </span>
        {extra}
      </div>
      <div style={{ padding: '14px 16px 16px' }}>{children}</div>
    </div>
  );
}
